// Port of TrailNavigation/RouteProgress.swift (RouteProgress and RouteProgressCalculator).

import type { EpochMs, GeoCoordinate, StageKeyPoint, TrackPoint, TrailStage } from '../core/models';
import { bearing, distance as geoDistance, isValidCoordinate } from '../core/geo';
import { HikingTimeEstimator } from './hikingTimeEstimator';
import {
  OffRouteConfiguration,
  OffRouteDetector,
  isOffRouteState,
  type OffRouteEvent,
} from './offRouteDetector';
import type { RouteTrack } from './routeTrack';
import { StageLayout, type RouteKeyPoint, type StageSpan } from './stageLayout';

/** Snapshot of the hiker's progress along a route. */
export interface RouteProgress {
  /** Distance from the route start (m), clamped to the route. */
  distanceAlong: number;
  /** Distance left to the route end (m). */
  distanceRemaining: number;
  /** Ascent left to the route end (m, hysteresis applied). */
  ascentRemaining: number;
  /** Descent left to the route end (m, hysteresis applied). */
  descentRemaining: number;
  /** Distance from the fix to the route (m). */
  crossTrackDistance: number;
  /** True while the off-route detector is in its confirmed off-route state. */
  isOffRoute: boolean;
  /** Stage currently being walked, when the route has stage metadata. */
  currentStageID?: string;
  /** Distance left to the end of the current stage (m). */
  stageDistanceRemaining?: number;
  /** Next stage key point strictly ahead (any stage), with `distanceFromStart` in travel direction. */
  nextKeyPoint?: StageKeyPoint;
  /** Distance to `nextKeyPoint` along the route (m). */
  distanceToNextKeyPoint?: number;
  /** Walking time (no breaks) to the route end with the personal factor applied (seconds). */
  estimatedTimeRemaining: number;
  /** `now + estimatedTimeRemaining`. */
  estimatedArrival: EpochMs;
  /** Elevation at the current position (m): the route profile, else the fix altitude. */
  currentElevation?: number;
  /** `distanceAlong / totalDistance` in 0…1 (0 for an empty route). */
  fractionComplete: number;
}

/** Result of `RouteProgressCalculator.update`. */
export interface RouteProgressUpdate {
  progress: RouteProgress;
  event?: OffRouteEvent;
}

/**
 * Turns GPS fixes into `RouteProgress` on a fixed route.
 *
 * Design choices:
 * - The last projected `distanceAlong` seeds a windowed projection (±1.5 km) on the next fix, so out-and-back sections
 *   and switchbacks do not make the position jump (see `RouteTrack.project`). The direction of travel — the GPS course
 *   when the fix is moving (≥ 0.5 m/s), otherwise the bearing from the last fix at least `max(15 m, accuracy)` away —
 *   additionally disambiguates the turnaround of an out-and-back.
 * - Fixes with an invalid coordinate or negative (invalid) accuracy, and fixes older than the last processed one, are
 *   ignored: the previous progress is returned and no event fires.
 * - Stage key points are mapped onto the route through the route's stage layout; for reversed routes their offsets are
 *   mirrored inside each stage.
 * - `estimatedTimeRemaining`/`estimatedArrival` refer to the **route end**; the current stage is described by
 *   `currentStageID` and `stageDistanceRemaining`.
 * - `isOffRoute` reflects the integrated `OffRouteDetector` state, also in `progress(…)`.
 */
export class RouteProgressCalculator {
  /** Projection window (m) around the previous position. */
  static readonly projectionWindow = 1500;
  /** Minimum GPS speed (m/s) for the reported course to be trusted. */
  static readonly minimumCourseSpeed = 0.5;
  /** Minimum displacement (m) for a bearing between two fixes to count as a direction of travel. */
  static readonly minimumCourseDisplacement = 15;

  /** The route geometry. */
  readonly track: RouteTrack;
  /** Time estimator; may be replaced as the personal factor is learned during the trip. */
  estimator: HikingTimeEstimator;

  readonly spans: readonly StageSpan[];
  readonly keyPoints: readonly RouteKeyPoint[];

  private detector: OffRouteDetector;
  private lastAlong: number | undefined;
  private lastCrossTrack = 0;
  private lastFixTimestamp: EpochMs | undefined;
  private lastFixElevation: number | undefined;
  private courseAnchor: GeoCoordinate | undefined;

  /**
   * Creates a calculator. `stages` supplies key points and stage lengths; it may be in any order and contain more
   * stages than the route (matched by id).
   */
  constructor(
    track: RouteTrack,
    stages: readonly TrailStage[],
    estimator: HikingTimeEstimator = new HikingTimeEstimator(),
    offRoute: Readonly<OffRouteConfiguration> = OffRouteConfiguration.default,
  ) {
    this.track = track;
    this.estimator = estimator;
    this.detector = new OffRouteDetector(offRoute);
    this.spans = StageLayout.spans(track, stages);
    this.keyPoints = StageLayout.keyPoints(this.spans);
  }

  /** The integrated off-route detector. */
  get offRouteDetector(): OffRouteDetector {
    return this.detector;
  }

  /** Distance along the route of the last processed fix, if any. */
  get lastDistanceAlong(): number | undefined {
    return this.lastAlong;
  }

  /** Processes a GPS fix and returns the new progress plus an off-route transition event, if any. */
  update(fix: TrackPoint, now: EpochMs): RouteProgressUpdate {
    if (!RouteProgressCalculator.isUsable(fix)) return { progress: this.currentProgress(now) };
    if (this.lastFixTimestamp !== undefined && fix.timestamp < this.lastFixTimestamp) {
      return { progress: this.currentProgress(now) };
    }

    const course = this.travelCourse(fix);
    const projection = this.track.project(
      fix.coordinate,
      this.lastAlong,
      RouteProgressCalculator.projectionWindow,
      course,
    );
    if (!projection) return { progress: this.currentProgress(now) };
    this.lastAlong = projection.distanceAlong;
    this.lastCrossTrack = projection.crossTrackDistance;
    this.lastFixTimestamp = fix.timestamp;
    this.lastFixElevation = fix.elevation !== undefined && Number.isFinite(fix.elevation) ? fix.elevation : undefined;

    const event = this.detector.evaluate(projection.crossTrackDistance, fix.horizontalAccuracy, fix.timestamp);
    return { progress: this.currentProgress(now), event };
  }

  /**
   * Progress at an arbitrary distance along the route (clamped). `isOffRoute` reflects the detector's current state.
   * (Swift `progress(at:crossTrack:now:)`; `crossTrack` defaults to 0.)
   */
  progress(distanceAlong: number, now: EpochMs, crossTrack = 0): RouteProgress {
    const track = this.track;
    const total = track.totalDistance;
    const distance = Number.isFinite(distanceAlong) ? Math.min(Math.max(distanceAlong, 0), total) : 0;
    const remaining = Math.max(0, total - distance);
    const { ascent, descent } = track.ascentDescent(distance, total);
    const spanIndex = StageLayout.spanIndex(this.spans, distance);
    const span = spanIndex === undefined ? undefined : this.spans[spanIndex];
    const next = this.nextKeyPoint(distance);
    const duration = this.estimator.duration(remaining, ascent, descent);
    return {
      distanceAlong: distance,
      distanceRemaining: remaining,
      ascentRemaining: ascent,
      descentRemaining: descent,
      crossTrackDistance: Number.isFinite(crossTrack) ? Math.max(0, crossTrack) : 0,
      isOffRoute: isOffRouteState(this.detector.state),
      currentStageID: span?.stageID,
      stageDistanceRemaining: span ? Math.max(0, span.endDistance - distance) : undefined,
      nextKeyPoint: next?.keyPoint,
      distanceToNextKeyPoint: next ? next.routeDistance - distance : undefined,
      estimatedTimeRemaining: duration,
      estimatedArrival: now + duration * 1000,
      currentElevation: track.elevationAtDistance(distance),
      fractionComplete: total > 0 ? distance / total : 0,
    };
  }

  /**
   * Forgets the position history and off-route state (e.g. after travelling by bus or when the hiker restarts
   * elsewhere on the route). The next fix uses a full projection.
   */
  reset(): void {
    this.lastAlong = undefined;
    this.lastCrossTrack = 0;
    this.lastFixTimestamp = undefined;
    this.lastFixElevation = undefined;
    this.courseAnchor = undefined;
    this.detector.reset();
  }

  // MARK: Private

  private currentProgress(now: EpochMs): RouteProgress {
    const progress = this.progress(this.lastAlong ?? 0, now, this.lastCrossTrack);
    if (progress.currentElevation === undefined) progress.currentElevation = this.lastFixElevation;
    return progress;
  }

  private nextKeyPoint(distance: number): RouteKeyPoint | undefined {
    // First key point strictly ahead.
    const keyPoints = this.keyPoints;
    let lo = 0;
    let hi = keyPoints.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (keyPoints[mid].routeDistance <= distance) lo = mid + 1;
      else hi = mid;
    }
    return lo < keyPoints.length ? keyPoints[lo] : undefined;
  }

  /** Direction of travel for disambiguating overlapping route sections, and course-anchor upkeep. */
  private travelCourse(fix: TrackPoint): number | undefined {
    const minimumDisplacement = Math.max(
      RouteProgressCalculator.minimumCourseDisplacement,
      fix.horizontalAccuracy ?? 0,
    );
    let bearingFromAnchor: number | undefined;
    const anchor = this.courseAnchor;
    if (anchor) {
      if (geoDistance(anchor, fix.coordinate) >= minimumDisplacement) {
        bearingFromAnchor = bearing(anchor, fix.coordinate);
        this.courseAnchor = fix.coordinate;
      }
    } else {
      this.courseAnchor = fix.coordinate;
    }
    const { course, speed } = fix;
    if (
      course !== undefined &&
      Number.isFinite(course) &&
      course >= 0 &&
      speed !== undefined &&
      Number.isFinite(speed) &&
      speed >= RouteProgressCalculator.minimumCourseSpeed
    ) {
      return course % 360;
    }
    return bearingFromAnchor;
  }

  private static isUsable(fix: TrackPoint): boolean {
    if (!isValidCoordinate(fix.coordinate) || !Number.isFinite(fix.timestamp)) return false;
    const accuracy = fix.horizontalAccuracy;
    if (accuracy !== undefined && !(Number.isFinite(accuracy) && accuracy >= 0)) return false;
    return true;
  }
}
